import prismicColors from "@/lib/prismicio/prismic-colors.json"
import { useTextConversionMethod } from "@/lib/prismicio/useTextConversion"
import { CheckboxIndicator, CheckboxRoot, ColorSquare, TickYes, cn, useFacetExcessive } from "@jog/react-components"
import { colorLanguageConverter, gtmFacetSearch } from "@jog/shared"
import type { Facet, FacetAttribute } from "@jog/types"
import { type CSSProperties, useState } from "react"

import { BOX_CLASSNAME } from "../FormField/const"

const Checkbox = ({
    pending,
    attribute,
    searchValue,
    facet,
    onChange,
}: {
    pending: boolean
    attribute: FacetAttribute
    searchValue: string
    facet: Facet
    onChange: (v: string) => void
}) => {
    const [checked, setChecked] = useState(attribute.isselected)
    return (
        <CheckboxRoot
            className="mr-1.25 h-3 w-3 shrink-0 border border-black bg-white"
            disabled={pending}
            value={`${facet.facetsettings.title}-${attribute.title}`}
            id={`${facet.facetsettings.title}-${attribute.title}`}
            checked={checked}
            onCheckedChange={(value) => {
                if (!attribute.url) return
                setChecked(value === "indeterminate" ? !checked : value)
                if (searchValue) gtmFacetSearch(value ? "check" : "uncheck", facet, attribute, searchValue)
                onChange?.(attribute.url)
            }}>
            <CheckboxIndicator className="flex items-center justify-center text-current">
                <TickYes className="h-2.5 w-2.5" />
            </CheckboxIndicator>
        </CheckboxRoot>
    )
}
export const FacetSearch = () => {
    const { facet, searchable, searchValue, setSearchValue, attributes } = useFacetExcessive()
    if (!searchable) return <></>
    return (
        <>
            <div className={cn(BOX_CLASSNAME, "mb-2 w-full bg-white")}>
                <input
                    className="w-full focus:outline-0"
                    type="text"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder={facet.facetsettings.searchplaceholder}
                />
            </div>
            {attributes.length === 0 && (
                <div className="py-1 text-sm text-gray-main">{facet.facetsettings.searchnoresultstext}</div>
            )}
        </>
    )
}
interface SquareStyle extends CSSProperties {
    ["--backgroundColor"]: string
}
export const CheckboxFacet = ({ pending, onChange }: { pending: boolean; onChange: (v: string) => void }) => {
    const textConvert = useTextConversionMethod()
    const { display, Showmore, facet, attributes, searchValue } = useFacetExcessive()
    if (!facet) return
    return (
        <>
            <FacetSearch />
            <ul className="space-y-2 py-1">
                {attributes
                    .filter((_, index) => display(index))
                    .map((attribute) => (
                        <li key={attribute.url} className="flex items-center">
                            <Checkbox
                                pending={pending}
                                attribute={attribute}
                                searchValue={searchValue}
                                facet={facet}
                                onChange={onChange}
                            />
                            <label
                                htmlFor={`${facet.facetsettings.title}-${attribute.title}`}
                                className="flex cursor-pointer items-center text-13">
                                {prismicColors.facets.includes(facet.facetsettings.title) && (
                                    <ColorSquare
                                        width={13}
                                        height={13}
                                        strokeWidth={2}
                                        className="mr-1.25 fill-[var(--backgroundColor)] stroke-grey-lighter"
                                        style={
                                            {
                                                "--backgroundColor": colorLanguageConverter(
                                                    attribute.title,
                                                    prismicColors.colors
                                                ),
                                                colorScheme: "only light",
                                            } as SquareStyle
                                        }
                                    />
                                )}
                                <span>
                                    {textConvert(attribute.title)}
                                    <span className="pl-1.25 text-gray-main">({attribute.nrofresults})</span>
                                </span>
                            </label>
                        </li>
                    ))}
            </ul>
            <Showmore className="mt-1.25 cursor-pointer py-1 text-xs lg:hover:text-red-mona" />
        </>
    )
}
